// ArticleEditPage — full-screen block-based editor for an article.
//
// Layout (three columns, fixed top bar):
//   ┌──────────────────────────────────────────────────────────────────┐
//   │ TopBar: back · title · status · save · words · preview · ⌘? · ⚙ │
//   ├────────────────────────────┬──────────────────────────┬──────────┤
//   │  Center column (max 720,   │  Preview pane (when on,  │ Settings │
//   │  scroll):                  │  50% split):             │ (320)    │
//   │    • cover                 │   read-only render of    │          │
//   │    • title input            │   the doc                 │          │
//   │    • summary                │                          │          │
//   │    • BlockEditor            │                          │          │
//   └────────────────────────────┴──────────────────────────┴──────────┘
//
// New in this revision:
//   • Dark theme everywhere — re-uses CSS variables from styles.css.
//   • Split preview pane (toggle from top bar).
//   • Keyboard shortcuts cheat-sheet modal (? button).
//   • Word count and estimated read-time in the top bar.
//   • Undo / Redo (Cmd+Z / Cmd+Shift+Z) with a 50-step ring buffer.
//   • BlockMenu now supports templates and categories — accessed via
//     the same "+" buttons.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  getOne,
  list,
  createRecord,
  updateRecord,
  fileUrl,
} from "../lib/pb";
import { navigate } from "../App";
import type {
  Article,
  ArticleKind,
  ArticleStatus,
  Editor,
} from "../lib/types";
import {
  type Block,
  type BlockDocument,
  emptyDocument,
  validateBlock,
} from "../lib/blocks";
import { BlockEditor } from "../components/editor/BlockEditor";
import { SettingsPanel } from "../components/editor/SettingsPanel";
import { StatusButton } from "../components/editor/StatusButton";
import { t as theme } from "../components/editor/theme";
import { useT } from "../lib/i18n";
import { statusLabel } from "./ArticlesPage";

const AUTOSAVE_DELAY_MS = 10_000;
const HISTORY_LIMIT = 50;
const HISTORY_DEBOUNCE_MS = 500;

interface PageProps {
  articleId: string | "new";
  editor: Editor;
}

interface HistoryEntry {
  title: string;
  summary: string;
  doc: BlockDocument;
}

export default function ArticleEditPage({ articleId, editor }: PageProps) {
  const t = useT();
  const isNew = articleId === "new";

  // ── Core state ─────────────────────────────────────────────────────
  const [loading, setLoading] = useState(!isNew);
  const [article, setArticle] = useState<Article | null>(null);
  const [editors, setEditors] = useState<Editor[]>([editor]);

  const [title, setTitle] = useState("");
  const [summary, setSummary] = useState("");
  const [doc, setDoc] = useState<BlockDocument>(emptyDocument());
  const [kind, setKind] = useState<ArticleKind>("article");
  const [status, setStatus] = useState<ArticleStatus>("draft");
  const [publishAt, setPublishAt] = useState("");
  const [tags, setTags] = useState<string[]>([]);
  const [isPremium, setIsPremium] = useState(false);
  const [featured, setFeatured] = useState(false);
  const [authorId, setAuthorId] = useState(editor.id);
  const [seriesId, setSeriesId] = useState("");
  const [seriesPosition, setSeriesPosition] = useState(1);
  const [pendingCover, setPendingCover] = useState<File | null>(null);

  const [mediaAssets, setMediaAssets] = useState<string[]>([]);
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  // ARCHIVED 2026-05-18: collapse SettingsPanel убран. Переменные остаются
  // для backward compat callbacks, но управление UI с ними отвязано.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [sidebarOpen, setSidebarOpen] = useState(true);
  void sidebarOpen; void setSidebarOpen;
  const [previewOpen, setPreviewOpen] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const persistedIdRef = useRef<string | null>(isNew ? null : articleId);
  const latestRef = useRef<{
    title: string;
    summary: string;
    doc: BlockDocument;
    kind: ArticleKind;
    status: ArticleStatus;
    publishAt: string;
    tags: string[];
    isPremium: boolean;
    featured: boolean;
    authorId: string;
    seriesId: string;
    seriesPosition: number;
  } | null>(null);
  latestRef.current = {
    title, summary, doc, kind, status, publishAt, tags,
    isPremium, featured, authorId, seriesId, seriesPosition,
  };

  const dirtyRef = useRef(false);
  const autosaveTimerRef = useRef<number | null>(null);
  const lastLoadedSnapshotRef = useRef<string>("");

  // ── History (undo/redo) ───────────────────────────────────────────
  // Stack of snapshots; pointer indexes the "current" entry. Pushing a new
  // entry truncates anything above the pointer (standard editor model).
  const historyRef = useRef<HistoryEntry[]>([]);
  const historyPointerRef = useRef<number>(-1);
  const historyTimerRef = useRef<number | null>(null);
  const restoringRef = useRef(false);

  function pushHistory(force = false) {
    if (restoringRef.current) return;
    const entry: HistoryEntry = {
      title: latestRef.current!.title,
      summary: latestRef.current!.summary,
      doc: JSON.parse(JSON.stringify(latestRef.current!.doc)),
    };
    const top = historyRef.current[historyPointerRef.current];
    if (top && !force) {
      // Skip if the snapshot hasn't actually changed.
      if (
        top.title === entry.title &&
        top.summary === entry.summary &&
        JSON.stringify(top.doc) === JSON.stringify(entry.doc)
      ) return;
    }
    // Drop any redo branch.
    historyRef.current = historyRef.current.slice(0, historyPointerRef.current + 1);
    historyRef.current.push(entry);
    if (historyRef.current.length > HISTORY_LIMIT) {
      historyRef.current.shift();
    }
    historyPointerRef.current = historyRef.current.length - 1;
  }

  function undo() {
    if (historyPointerRef.current <= 0) return;
    historyPointerRef.current -= 1;
    const e = historyRef.current[historyPointerRef.current];
    restoringRef.current = true;
    setTitle(e.title);
    setSummary(e.summary);
    setDoc(e.doc);
    setTimeout(() => { restoringRef.current = false; }, 0);
  }

  function redo() {
    if (historyPointerRef.current >= historyRef.current.length - 1) return;
    historyPointerRef.current += 1;
    const e = historyRef.current[historyPointerRef.current];
    restoringRef.current = true;
    setTitle(e.title);
    setSummary(e.summary);
    setDoc(e.doc);
    setTimeout(() => { restoringRef.current = false; }, 0);
  }

  // ── Load ───────────────────────────────────────────────────────────

  useEffect(() => {
    list<Editor>("editors", { perPage: 100, sort: "name" })
      .then((r) => setEditors(r.items))
      .catch(() => setEditors([editor]));
  }, [editor]);

  useEffect(() => {
    if (isNew) {
      // Seed history with empty snapshot.
      historyRef.current = [{ title: "", summary: "", doc: emptyDocument() }];
      historyPointerRef.current = 0;
      return;
    }
    setLoading(true);
    getOne<Article>("articles", articleId, "tags")
      .then((a) => {
        setArticle(a);
        setTitle(a.title);
        setSummary(a.summary);
        setKind(a.kind);
        setStatus(a.status);
        setPublishAt(a.publish_at ? toLocalInput(a.publish_at) : "");
        setTags(a.tags || []);
        setIsPremium(a.is_premium);
        setFeatured(a.featured);
        setAuthorId(a.author);
        setSeriesId(a.series || "");
        setSeriesPosition(a.series_position || 1);
        setMediaAssets(a.media_assets || []);
        const blocks = parseDoc((a as unknown as { body_blocks?: string | BlockDocument }).body_blocks);
        setDoc(blocks);
        lastLoadedSnapshotRef.current = snapshot({
          title: a.title,
          summary: a.summary,
          doc: blocks,
          kind: a.kind,
          status: a.status,
          publishAt: a.publish_at ? toLocalInput(a.publish_at) : "",
          tags: a.tags || [],
          isPremium: a.is_premium,
          featured: a.featured,
          authorId: a.author,
          seriesId: a.series || "",
          seriesPosition: a.series_position || 1,
        });
        persistedIdRef.current = a.id;
        // Seed history.
        historyRef.current = [{ title: a.title, summary: a.summary, doc: blocks }];
        historyPointerRef.current = 0;
      })
      .catch((e) => {
        alert(`${t.editLoadFailed}: ${(e as Error).message}`);
        navigate("/articles");
      })
      .finally(() => setLoading(false));
  }, [articleId, isNew]);

  // ── Dirty tracking + autosave + history snapshot ───────────────────

  useEffect(() => {
    if (loading) return;
    const snap = snapshot({
      title, summary, doc, kind, status, publishAt, tags,
      isPremium, featured, authorId, seriesId, seriesPosition,
    });
    if (snap !== lastLoadedSnapshotRef.current || pendingCover) {
      dirtyRef.current = true;
      if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
      autosaveTimerRef.current = window.setTimeout(() => {
        void save({ silent: true });
      }, AUTOSAVE_DELAY_MS);
    }
    // Debounce history snapshot.
    if (historyTimerRef.current) clearTimeout(historyTimerRef.current);
    historyTimerRef.current = window.setTimeout(() => pushHistory(), HISTORY_DEBOUNCE_MS);
    return () => {
      if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
      if (historyTimerRef.current) clearTimeout(historyTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    title, summary, doc, kind, status, publishAt, tags, isPremium,
    featured, authorId, seriesId, seriesPosition, pendingCover, loading,
  ]);

  // Keyboard shortcuts.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.metaKey || e.ctrlKey) {
        const k = e.key.toLowerCase();
        if (k === "s") {
          e.preventDefault();
          void save({ showToast: true });
          return;
        }
        if (k === "z" && !e.shiftKey) {
          e.preventDefault();
          undo();
          return;
        }
        if ((k === "z" && e.shiftKey) || k === "y") {
          e.preventDefault();
          redo();
          return;
        }
      }
      if (e.key === "?" && (e.shiftKey || e.metaKey || e.ctrlKey)) {
        // Cmd+? or Shift+? toggles shortcuts.
        e.preventDefault();
        setShortcutsOpen((v) => !v);
      }
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Save ───────────────────────────────────────────────────────────

  function buildFormData(opts: {
    forCreate: boolean;
    overrideStatus?: ArticleStatus;
    overridePublishAt?: string;
  }): FormData {
    const fd = new FormData();
    const cur = latestRef.current!;
    fd.append("title", cur.title.trim() || "(untitled)");
    fd.append("slug", slugify(cur.title || `draft-${Date.now()}`));
    fd.append("kind", cur.kind);
    fd.append("summary", cur.summary.trim());
    fd.append("body_blocks", JSON.stringify(cur.doc));
    if (article?.body_markdown) {
      fd.append("body_markdown", article.body_markdown);
    }
    fd.append(
      "status",
      opts.overrideStatus !== undefined ? opts.overrideStatus : cur.status,
    );
    const pat = opts.overridePublishAt !== undefined ? opts.overridePublishAt : cur.publishAt;
    fd.append("publish_at", pat ? new Date(pat).toISOString() : "");
    cur.tags.forEach((tId) => fd.append("tags", tId));
    if (cur.tags.length === 0) fd.append("tags", "");
    fd.append("is_premium", String(cur.isPremium));
    fd.append("featured", String(cur.featured));
    fd.append("author", cur.authorId || editor.id);
    fd.append("series", cur.seriesId || "");
    fd.append("series_position", String(cur.seriesId ? cur.seriesPosition || 1 : 0));
    void opts.forCreate;
    if (pendingCover) fd.append("cover", pendingCover);
    return fd;
  }

  async function save(opts: {
    silent?: boolean;
    showToast?: boolean;
    overrideStatus?: ArticleStatus;
    overridePublishAt?: string;
  } = {}): Promise<Article | null> {
    if (!dirtyRef.current && !opts.overrideStatus && !opts.overridePublishAt) {
      return article;
    }
    setSaving(true);
    setSaveError(null);
    try {
      let saved: Article;
      if (!persistedIdRef.current) {
        const fd = buildFormData({ forCreate: true, ...opts });
        saved = await createRecord<Article>("articles", fd);
        persistedIdRef.current = saved.id;
        navigate(`/article/${saved.id}`);
      } else {
        const fd = buildFormData({ forCreate: false, ...opts });
        saved = await updateRecord<Article>(
          "articles",
          persistedIdRef.current,
          fd,
        );
      }
      setArticle(saved);
      setMediaAssets(saved.media_assets || []);
      setPendingCover(null);
      lastLoadedSnapshotRef.current = snapshot({
        title: saved.title,
        summary: saved.summary,
        doc: parseDoc((saved as unknown as { body_blocks?: string | BlockDocument }).body_blocks),
        kind: saved.kind,
        status: saved.status,
        publishAt: saved.publish_at ? toLocalInput(saved.publish_at) : "",
        tags: saved.tags || [],
        isPremium: saved.is_premium,
        featured: saved.featured,
        authorId: saved.author,
        seriesId: saved.series || "",
        seriesPosition: saved.series_position || 1,
      });
      dirtyRef.current = false;
      setSavedAt(new Date());
      if (opts.showToast) flashToast(t.editSaved);
      return saved;
    } catch (e) {
      const msg = (e as Error).message;
      setSaveError(msg);
      if (!opts.silent) alert(`${t.editSaveFailedPrefix}: ${msg}`);
      return null;
    } finally {
      setSaving(false);
    }
  }

  function flashToast(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(null), 1800);
  }

  const onUploadFile = useCallback(
    async (file: File): Promise<string> => {
      if (!persistedIdRef.current) {
        const created = await save({ silent: true });
        if (!created) throw new Error("Couldn't create article for upload");
      }
      const id = persistedIdRef.current!;
      const fd = new FormData();
      fd.append("media_assets+", file);
      const updated = await updateRecord<Article>("articles", id, fd);
      const fresh = updated.media_assets || [];
      setMediaAssets(fresh);
      const prev = new Set(mediaAssets);
      const added = fresh.find((f) => !prev.has(f));
      if (!added) return fresh[fresh.length - 1] || file.name;
      return added;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mediaAssets],
  );

  // ── Computed: publish gate + word count ────────────────────────────

  const publishProblem = useMemo<string | null>(() => {
    if (!title.trim()) return "Title is empty";
    if (!summary.trim()) return "Summary is empty";
    if (doc.blocks.length === 0) return "No blocks yet";
    for (const b of doc.blocks) {
      const msg = validateBlock(b);
      if (msg) return `Block ${b.type}: ${msg}`;
    }
    return null;
  }, [title, summary, doc]);

  const canPublish = publishProblem === null;

  const wordStats = useMemo(() => {
    let words = 0;
    for (const b of doc.blocks) {
      if (b.type === "paragraph") words += countWords(stripHtml(b.html));
      else if (b.type === "heading" || b.type === "quote" || b.type === "caption") {
        words += countWords(b.text);
      } else if (b.type === "list") {
        for (const it of b.items) words += countWords(it);
      }
    }
    if (title) words += countWords(title);
    if (summary) words += countWords(summary);
    const minutes = Math.max(1, Math.round(words / 200));
    return { words, minutes };
  }, [doc, title, summary]);

  // ── Render ────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="editor-shell" style={{ position: "relative" }}>
        <div style={{ padding: 40, color: theme.text3, fontFamily: theme.sans }}>
          {t.loading}
        </div>
      </div>
    );
  }

  return (
    <div
      className="editor-shell"
      style={{
        position: "relative",
        color: theme.text,
      }}
    >
      {/* ── Top bar ───────────────────────────────────────────── */}
      <div
        className="editor-topbar"
        style={{
          fontFamily: theme.sans,
        }}
      >
        <button
          type="button"
          onClick={() => navigate("/articles")}
          aria-label={t.editBack}
          className="btn btn-ghost btn-sm"
          style={{ padding: "4px 10px", fontSize: 16 }}
        >
          ←
        </button>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder={t.editTitlePlaceholder}
          style={{
            flex: 1,
            minWidth: 0,
            background: "transparent",
            border: "none",
            outline: "none",
            fontFamily: theme.sans,
            fontSize: 14,
            fontWeight: 500,
            color: theme.text,
            padding: "4px 6px",
            borderRadius: 4,
          }}
        />
        <span className={`badge badge-${status}`}>{statusLabel(t, status)}</span>
        <SaveIndicator
          saving={saving}
          savedAt={savedAt}
          error={saveError}
          dirty={dirtyRef.current}
        />
        <span style={{ fontSize: 11, color: theme.text3, padding: "0 8px", borderLeft: `1px solid ${theme.border}`, marginLeft: 4 }}>
          {t.editWords(wordStats.words)} · {t.editReadTime(wordStats.minutes)}
        </span>
        <button
          type="button"
          onClick={() => setPreviewOpen((v) => !v)}
          className={previewOpen ? "btn btn-secondary btn-sm" : "btn btn-ghost btn-sm"}
          title={t.editPreview}
        >
          {previewOpen ? t.editHidePreview : t.editPreview}
        </button>
        <button
          type="button"
          onClick={() => setShortcutsOpen(true)}
          className="btn btn-ghost btn-sm"
          title={t.editShortcuts}
          style={{ width: 28, padding: 0 }}
        >
          ?
        </button>
        <StatusButton
          canPublish={canPublish}
          canPublishReason={publishProblem}
          onSaveDraft={() => save({ showToast: true, overrideStatus: "draft" })}
          onPublishNow={() => save({ showToast: true, overrideStatus: "published" })}
          onSchedule={(dt) => save({ showToast: true, overrideStatus: "scheduled", overridePublishAt: dt })}
        />
        {/* ARCHIVED 2026-05-18: restore-кнопка для свёрнутого Settings-панели
            убрана — collapse функция вообще убрана. Панель всегда видима. */}
      </div>

      {/* ── Body ──────────────────────────────────────────────── */}
      <div className="editor-body">
        <div className="editor-content">
          <div className="editor-content-inner">
            {article?.cover && !pendingCover && (
              <img
                src={fileUrl("articles", article.id, article.cover, "1200x600")}
                alt=""
                style={{
                  width: "100%",
                  aspectRatio: "16 / 9",
                  objectFit: "cover",
                  borderRadius: 10,
                  marginBottom: 24,
                  background: theme.borderStrong,
                  border: `1px solid ${theme.border}`,
                }}
              />
            )}
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t.editTitlePlaceholder}
              style={{
                width: "100%",
                boxSizing: "border-box",
                background: "transparent",
                border: "none",
                outline: "none",
                fontFamily: theme.serif,
                fontSize: 38,
                fontWeight: 600,
                letterSpacing: "-0.02em",
                color: theme.text,
                lineHeight: 1.15,
                padding: "4px 0",
                marginBottom: 12,
              }}
            />
            <textarea
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              placeholder={t.editSummaryPlaceholder}
              rows={2}
              style={{
                width: "100%",
                boxSizing: "border-box",
                background: "transparent",
                border: "none",
                outline: "none",
                resize: "vertical",
                fontFamily: theme.serif,
                fontSize: 18,
                lineHeight: 1.5,
                color: theme.text3,
                padding: "4px 0",
                marginBottom: 24,
              }}
            />

            <BlockEditor
              doc={doc}
              onChange={setDoc}
              articleId={persistedIdRef.current || ""}
              mediaAssets={mediaAssets}
              onUploadFile={onUploadFile}
            />
          </div>
        </div>

        {previewOpen && (
          <PreviewPane
            title={title}
            summary={summary}
            doc={doc}
            article={article}
            mediaAssets={mediaAssets}
          />
        )}

        {/* SettingsPanel — теперь всегда видим (collapse функция убрана 2026-05-18). */}
        <SettingsPanel
          article={article}
          editors={editors}
          selectedKind={kind}
          onKindChange={setKind}
          status={status}
          onStatusChange={setStatus}
          publishAt={publishAt}
          onPublishAtChange={setPublishAt}
          tags={tags}
          onTagsChange={setTags}
          isPremium={isPremium}
          onPremiumChange={setIsPremium}
          featured={featured}
          onFeaturedChange={setFeatured}
          authorId={authorId}
          onAuthorChange={setAuthorId}
          seriesId={seriesId}
          onSeriesChange={setSeriesId}
          seriesPosition={seriesPosition}
          onSeriesPositionChange={setSeriesPosition}
          pendingCover={pendingCover}
          onPendingCoverChange={setPendingCover}
          onClose={() => {/* archived — see SettingsPanel header */}}
        />
      </div>

      {shortcutsOpen && <ShortcutsModal onClose={() => setShortcutsOpen(false)} />}

      {toast && (
        <div
          style={{
            position: "fixed",
            bottom: 24,
            left: "50%",
            transform: "translateX(-50%)",
            padding: "8px 16px",
            background: theme.cardRaised,
            color: theme.text,
            border: `1px solid ${theme.border}`,
            borderRadius: 6,
            fontSize: 12,
            fontFamily: theme.sans,
            boxShadow: "0 6px 20px rgba(0,0,0,0.45)",
            zIndex: 1100,
          }}
        >
          {toast}
        </div>
      )}
    </div>
  );
}

// ── Preview pane (minimal renderer) ──────────────────────────────────

function PreviewPane({
  title,
  summary,
  doc,
  article,
  mediaAssets,
}: {
  title: string;
  summary: string;
  doc: BlockDocument;
  article: Article | null;
  mediaAssets: string[];
}) {
  const t = useT();
  void mediaAssets;
  return (
    <div
      style={{
        flex: 1,
        overflow: "auto",
        borderLeft: `1px solid ${theme.border}`,
        background: theme.bg,
        padding: "32px 24px 120px",
      }}
    >
      <div style={{ maxWidth: 640, margin: "0 auto" }}>
        <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: "0.14em", textTransform: "uppercase", color: theme.accent, marginBottom: 8 }}>
          {t.editPreview}
        </div>
        {article?.cover && (
          <img
            src={fileUrl("articles", article.id, article.cover, "1200x600")}
            alt=""
            style={{ width: "100%", aspectRatio: "16/9", objectFit: "cover", borderRadius: 10, marginBottom: 22 }}
          />
        )}
        <h1 style={{ fontFamily: theme.serif, fontSize: 32, fontWeight: 600, letterSpacing: "-0.02em", color: theme.text, lineHeight: 1.15, marginBottom: 12 }}>
          {title || "Untitled"}
        </h1>
        <p style={{ fontFamily: theme.serif, fontSize: 17, lineHeight: 1.5, color: theme.text2, marginBottom: 24 }}>
          {summary}
        </p>
        <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
          {doc.blocks.map((b) => <PreviewBlock key={b.id} block={b} article={article} />)}
        </div>
      </div>
    </div>
  );
}

function PreviewBlock({ block, article }: { block: Block; article: Article | null }) {
  switch (block.type) {
    case "heading": {
      const sizes = { 1: 26, 2: 22, 3: 18 } as const;
      const style: React.CSSProperties = {
        fontFamily: theme.serif,
        fontSize: sizes[block.level] || 20,
        fontWeight: 600,
        color: theme.text,
        letterSpacing: "-0.01em",
        lineHeight: 1.25,
        textAlign: block.align || "left",
      };
      const inner = block.text || <span style={{ color: theme.text4 }}>(empty heading)</span>;
      if (block.level === 1) return <h1 style={style}>{inner}</h1>;
      if (block.level === 3) return <h3 style={style}>{inner}</h3>;
      return <h2 style={style}>{inner}</h2>;
    }
    case "paragraph":
      return (
        <p
          style={{ fontFamily: theme.serif, fontSize: 16, lineHeight: 1.7, color: theme.text2, textAlign: block.align || "left" }}
          dangerouslySetInnerHTML={{ __html: block.html || `<span style="color:${theme.text4}">(empty paragraph)</span>` }}
        />
      );
    case "quote":
      return (
        <blockquote style={{ borderLeft: `3px solid ${theme.accent}`, paddingLeft: 16, fontFamily: theme.serif, fontStyle: "italic", fontSize: 17, color: theme.text2, lineHeight: 1.55 }}>
          {block.text}
          {block.attribution && <div style={{ fontStyle: "normal", fontSize: 12, color: theme.text3, marginTop: 8 }}>— {block.attribution}</div>}
        </blockquote>
      );
    case "image": {
      const src = block.url || (block.file && article ? fileUrl("articles", article.id, block.file, "800x800") : "");
      return src ? (
        <figure style={{ margin: 0 }}>
          <img src={src} alt={block.alt || ""} style={{ width: "100%", borderRadius: 6 }} />
          {block.caption && <figcaption style={{ fontSize: 12, color: theme.text3, textAlign: "center", marginTop: 6 }}>{block.caption}</figcaption>}
        </figure>
      ) : <PreviewPlaceholder label="Image" />;
    }
    case "gallery":
      return <PreviewPlaceholder label={`Gallery · ${(block.items?.length || 0) + (block.files?.length || 0)} items`} />;
    case "video_embed":
      return <PreviewPlaceholder label={`Video embed · ${block.provider || "auto"}`} />;
    case "video_file":
      return <PreviewPlaceholder label={`Video file · ${block.file || "—"}`} />;
    case "audio_embed":
      return <PreviewPlaceholder label={`Audio embed · ${block.provider || "auto"}`} />;
    case "audio_file":
      return <PreviewPlaceholder label={`Audio file · ${block.file || "—"}`} />;
    case "link_card":
      return <PreviewPlaceholder label={`Link card · ${block.title || block.url}`} />;
    case "caption":
      return <p style={{ fontSize: 12, color: theme.text3, textAlign: block.align || "center", fontStyle: "italic" }}>{block.text}</p>;
    case "divider":
      return <hr style={{ border: "none", borderTop: `1px solid ${theme.border}`, margin: "8px 0" }} />;
    case "list":
      return block.ordered ? (
        <ol style={{ paddingLeft: 22, color: theme.text2, fontFamily: theme.serif, lineHeight: 1.6 }}>
          {block.items.map((it, i) => <li key={i}>{it}</li>)}
        </ol>
      ) : (
        <ul style={{ paddingLeft: 22, color: theme.text2, fontFamily: theme.serif, lineHeight: 1.6 }}>
          {block.items.map((it, i) => <li key={i}>{it}</li>)}
        </ul>
      );
    case "spacer":
      return <div style={{ height: block.height }} />;
    case "cta":
      return (
        <div style={{ textAlign: block.align || "left" }}>
          <a href={block.url || "#"} style={{ display: "inline-block", padding: "10px 20px", background: theme.accent, color: "#1c1c1e", borderRadius: 6, fontWeight: 600, fontSize: 13 }}>
            {block.label || "Action"}
          </a>
        </div>
      );
    case "hero":
      return <PreviewPlaceholder label={`Hero · ${block.title || block.media || "no media"}`} />;
    case "embed":
      return <PreviewPlaceholder label="Embed (raw iframe)" />;
  }
}

function PreviewPlaceholder({ label }: { label: string }) {
  return (
    <div
      style={{
        padding: "20px 14px",
        background: theme.card,
        border: `1px dashed ${theme.border}`,
        borderRadius: 6,
        color: theme.text3,
        fontSize: 12,
        fontFamily: theme.sans,
        textAlign: "center",
      }}
    >
      {label}
    </div>
  );
}

// ── Shortcuts modal ──────────────────────────────────────────────────

function ShortcutsModal({ onClose }: { onClose: () => void }) {
  const t = useT();
  const rows: Array<[string, string]> = [
    ["⌘ S", t.shortcutsSave],
    ["⌘ Z", t.shortcutsUndo],
    ["⌘ ⇧ Z", t.shortcutsRedo],
    ["/", t.shortcutsInsertBlock],
    ["⌘ ⏎", t.shortcutsPublish],
    ["Esc", t.shortcutsClose],
    ["⇧ ?", t.shortcutsCheatTitle],
  ];
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" style={{ width: 460 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <span className="modal-title">{t.editShortcuts}</span>
          <button onClick={onClose} className="btn btn-ghost btn-sm">×</button>
        </div>
        <div className="modal-body">
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <tbody>
              {rows.map(([k, v]) => (
                <tr key={k} style={{ borderBottom: `1px solid ${theme.border}` }}>
                  <td style={{ padding: "10px 4px", width: 140 }}><span className="kbd">{k}</span></td>
                  <td style={{ padding: "10px 4px", fontSize: 12, color: theme.text2 }}>{v}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="modal-footer">
          <button onClick={onClose} className="btn btn-secondary">{t.close}</button>
        </div>
      </div>
    </div>
  );
}

// ── Save indicator ────────────────────────────────────────────────────

function SaveIndicator({
  saving, savedAt, error, dirty,
}: {
  saving: boolean;
  savedAt: Date | null;
  error: string | null;
  dirty: boolean;
}) {
  const t = useT();
  if (saving) return <Indicator color={theme.text3} label={t.editSaving} />;
  if (error) return <Indicator color={theme.danger} label={t.editSaveFailed} title={error} />;
  if (dirty) return <Indicator color={theme.text3} label={t.editUnsaved} />;
  if (savedAt) {
    const ago = Math.max(1, Math.round((Date.now() - savedAt.getTime()) / 1000));
    const agoLabel = ago < 60 ? `${ago}s ago` : savedAt.toLocaleTimeString();
    return (
      <Indicator
        color={theme.success}
        label={t.editSavedAgo(agoLabel)}
      />
    );
  }
  return null;
}

function Indicator({ color, label, title }: { color: string; label: string; title?: string }) {
  return (
    <span title={title} style={{ fontSize: 11, color, fontFamily: theme.sans }}>
      {label}
    </span>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────

function snapshot(s: {
  title: string;
  summary: string;
  doc: BlockDocument;
  kind: ArticleKind;
  status: ArticleStatus;
  publishAt: string;
  tags: string[];
  isPremium: boolean;
  featured: boolean;
  authorId: string;
  seriesId?: string;
  seriesPosition?: number;
}): string {
  return JSON.stringify(s);
}

function parseDoc(raw: unknown): BlockDocument {
  if (!raw) return emptyDocument();
  if (typeof raw === "string") {
    if (!raw.trim()) return emptyDocument();
    try {
      const parsed = JSON.parse(raw);
      if (parsed && parsed.schemaVersion && Array.isArray(parsed.blocks)) {
        return parsed as BlockDocument;
      }
    } catch {
      return emptyDocument();
    }
  }
  if (typeof raw === "object") {
    const p = raw as Partial<BlockDocument>;
    if (p && p.schemaVersion && Array.isArray(p.blocks)) {
      return p as BlockDocument;
    }
  }
  return emptyDocument();
}

function slugify(s: string): string {
  const map: Record<string, string> = {
    а: "a", б: "b", в: "v", г: "g", д: "d", е: "e", ё: "e", ж: "zh",
    з: "z", и: "i", й: "i", к: "k", л: "l", м: "m", н: "n", о: "o",
    п: "p", р: "r", с: "s", т: "t", у: "u", ф: "f", х: "h", ц: "c",
    ч: "ch", ш: "sh", щ: "sch", ъ: "", ы: "y", ь: "", э: "e", ю: "yu",
    я: "ya",
  };
  return s
    .toLowerCase()
    .split("")
    .map((c) => map[c] ?? c)
    .join("")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);
}

function toLocalInput(iso: string): string {
  try {
    const d = new Date(iso);
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch {
    return "";
  }
}

function countWords(s: string): number {
  if (!s) return 0;
  return s.trim().split(/\s+/).filter(Boolean).length;
}

function stripHtml(html: string): string {
  return html.replace(/<[^>]*>/g, " ");
}
